import React, { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useLensScanCost } from '~/hooks/apis/reporting/useLensScanCost';

import { formatScanBytes, formatScanUsd } from './scanCost';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  sql: string;
  executed?: boolean;
  parameters?: Record<string, unknown>;
  /** Named when the card reads a shared dataset rather than its own query. */
  datasetName?: string;
};

/**
 * The query behind a card, readable and copyable.
 *
 * A number nobody can trace is a number nobody trusts, and until now the only
 * way to see a card's SQL was to open the editor, which a viewer without edit
 * rights cannot do.
 */
export default function LensSqlDialog(props: Props) {
  return props.open ? <LensSqlDialogContent {...props} /> : null;
}

function LensSqlDialogContent({ open, onClose, title, sql, datasetName, executed = false, parameters }: Props) {
  const [copied, setCopied] = useState(false);
  const check = useLensScanCost();
  const signature = JSON.stringify({ sql, parameters });
  // Match results to their inputs so a changed query never displays an old estimate.
  const current = check.variables && JSON.stringify(check.variables) === signature;
  const estimate = current ? check.data : undefined;
  const close = () => {
    check.reset();
    onClose();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // A denied clipboard permission is not worth an error dialog: the SQL is
      // on screen and selectable, which is the fallback anyway.
      setCopied(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" onClose={close} open={open}>
      <DialogTitle>
        <Stack alignItems="center" direction="row" spacing={1}>
          <Typography component="span" sx={{ flex: 1 }} variant="h6">
            {title}
          </Typography>
          {datasetName && <Chip label={`Shared dataset: ${datasetName}`} size="small" variant="outlined" />}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ display: 'block', mb: 1 }} variant="caption">
          {executed
            ? 'This query produced the returned rows. Chart grouping and result filters may further shape what you see.'
            : 'Dataset source SQL. The executed query is not available for this result. Lens may apply additional query filters and chart grouping.'}
        </Typography>
        {parameters && Object.keys(parameters).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Query parameters</Typography>
            {Object.entries(parameters).map(([name, value]) => (
              <Typography key={name} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                @{name}: {typeof value === 'string' ? value : JSON.stringify(value)}
              </Typography>
            ))}
          </Box>
        )}
        <Box
          component="pre"
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: '8px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            lineHeight: 1.6,
            m: 0,
            maxHeight: '60vh',
            overflow: 'auto',
            p: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
          tabIndex={0}
        >
          {sql}
        </Box>
        <Box aria-live="polite" sx={{ mt: 2 }}>
          {estimate && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Estimated scan: {formatScanBytes(estimate.bytesProcessed)} · {formatScanUsd(estimate.estimatedUsd)} per
                uncached run
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Reference on-demand rate: US${estimate.usdPerTiB}/TiB. Actual billing varies with region, pricing plan,
                minimum charges and caching. This check does not run the query.
              </Typography>
            </>
          )}
          {current && check.isError && (
            <Alert severity="warning">
              {check.error instanceof Error ? check.error.message : 'Could not estimate this query.'}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
        <Button disabled={check.isPending || !sql.trim()} onClick={() => check.mutate({ sql, parameters })}>
          {check.isPending ? 'Checking scan…' : 'Check scan cost'}
        </Button>
        <Button onClick={copy} startIcon={copied ? <DoneIcon /> : <ContentCopyIcon />} variant="contained">
          {copied ? 'Copied' : 'Copy SQL'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
